import { METRIC_KEY } from 'helpers/dataHelpers/pivot';
import { OrderedColumnsT } from 'business/definitions';
import { YEAR_COLUMN_KEY } from 'business/constants';
import { getBestSuitableRemainingColumn } from './helpers';

/**
 * To initialize a multimetric cartesian chart.
 * We pick the multi-metric column on the z axis and pick the remaining one as x.
 */
const getMultimetricOrderedColumns = (
    chartColumns: ResponseColumnT[],
    metricColumn: ResponseColumnT,
): OrderedColumnsT => {
    const metricKeyColum = chartColumns.find(
        (col) =>
            col.key === METRIC_KEY ||
            (col.mergedColumns && col.key.includes(METRIC_KEY)),
    );
    if (!metricKeyColum) return { y: metricColumn };
    const remainingColumn = getBestSuitableRemainingColumn(chartColumns, [
        metricColumn.key,
        metricKeyColum.key,
    ]);
    return { x: remainingColumn, y: metricColumn, z: metricKeyColum };
};

/**
 * To initialize a yearly comparison cartesian chart, the year column is put on the z axis and the scale one on the x.
 */
const getYearlyComparisonOrderedColumns = (
    chartColumns: ResponseColumnT[],
    metricColumn: ResponseColumnT,
    yearlyComparisonScale: string,
): OrderedColumnsT => {
    const scaleColumn = chartColumns.find(
        (col) => col.key === yearlyComparisonScale,
    );
    const yearColumn = chartColumns.find(
        (col) =>
            col.key === YEAR_COLUMN_KEY ||
            (col.mergedColumns && col.key.includes(YEAR_COLUMN_KEY)),
    );
    if (!scaleColumn || !yearColumn) return { y: metricColumn };
    return {
        x: scaleColumn,
        y: metricColumn,
        z: yearColumn,
    };
};

/**
 * Finds the ordered columns of a cartesian visualization.
 *
 * As the conception is enabled for these types of chart, we do not focus as we used to on a perfect match of the user expectections.
 * This makes sure multimetric and yearly comparison cases are properly handled.
 * The base case simply consists in putting the most relevant column as x and the second most as z.
 */
export const getCartesianVizOrderedColumns = (
    chartColumns: ResponseColumnT[],
    metricColumn: ResponseColumnT,
    isMultimetric: boolean,
    yearlyComparisonScale: string | undefined,
): OrderedColumnsT => {
    // Handle multimetric and yearly comparison cases
    if (isMultimetric)
        return getMultimetricOrderedColumns(chartColumns, metricColumn);
    if (yearlyComparisonScale)
        return getYearlyComparisonOrderedColumns(
            chartColumns,
            metricColumn,
            yearlyComparisonScale,
        );
    // Base case
    const xColumn = getBestSuitableRemainingColumn(chartColumns, [
        metricColumn.key,
    ]);
    if (!xColumn) return { y: metricColumn };
    return {
        x: xColumn,
        y: metricColumn,
        z: getBestSuitableRemainingColumn(chartColumns, [
            metricColumn.key,
            xColumn.key,
        ]),
    };
};
